import { useEffect, useRef, useState } from "react";

import { Icon } from "~/assets/icons/Icon";

import Button from "../button/Button";
import Input from "../input/Input";
import styles from "./SearchBar.module.scss";

interface SearchBarProps {
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  width?: string | number;
  onFocus?: () => void;
  onBlur?: () => void;
  isShort?: boolean;
  endAdornment?: React.ReactNode;
  /**
   * Optional controlled collapse override. When set, the bar's collapsed
   * state is driven entirely by this prop (plus `isShort`) — internal
   * focus/value heuristics are ignored. Used by callers that need to
   * force-collapse the bar in response to external events (e.g. closing
   * a related modal) without fighting focus-trap timing.
   */
  forceCollapsed?: boolean;
  onExpandRequest?: () => void;
}

export const SearchBar = ({
  value,
  onChange,
  placeholder = "Search",
  isShort = false,
  endAdornment,
  forceCollapsed,
  onExpandRequest,
}: SearchBarProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const isCollapsed =
    forceCollapsed || (value?.length === 0 && !isFocused && isShort);

  // autofocus input
  useEffect(() => {
    if (isFocused && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isFocused]);

  return (
    <div
      className={`${styles.searchContainer} ${isCollapsed ? styles.collapsed : ""}`}
      style={{
        width: isCollapsed ? "46px" : "250px",
      }}
    >
      <Input
        style={{
          width: "100%",
          overflow: "hidden",
          cursor: isCollapsed ? "pointer" : undefined,
        }}
        ref={inputRef}
        value={value}
        ownProps={{ noErrors: true }}
        placeholder={isCollapsed ? "" : placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        startAdornment={
          <Button
            noHover
            onMouseDown={(e: React.MouseEvent) => {
              e.preventDefault();
              if (isCollapsed) {
                onExpandRequest?.();
                setIsFocused(true);
              } else {
                inputRef.current?.blur();
              }
            }}
          >
            <Icon
              iconName="SearchIcon"
              width="1rem"
              height="1.26rem"
              className={styles.button}
            />
          </Button>
        }
        endAdornment={endAdornment}
      />
    </div>
  );
};
